// Safety net for in-browser page translators (Edge, Chrome, Safari).
//
// A translator rewrites the text nodes React rendered: Edge swaps each one for
// a new node, Chrome and Safari keep it but wrap it in <font>. React still
// holds the originals, so its next deletion or placement against one of them
// (a string child that became "", a conditional text branch, an element
// inserted before a text sibling) throws NotFoundError from removeChild or
// insertBefore, and the nearest error boundary replaces the page. FIV-657 was
// exactly that: an Edge user in Harbin, homepage translated to Chinese, saw
// React Router's error page instead of the homepage.
//
// The proper fix is structural and lives in the components: volatile text goes
// in its own element, so React updates it through textContent and never deletes
// a bare text node (see the FIV-657 wraps in MembersSection, HeroHeadline,
// FormNavigation and CreatorReferralStrip). This module is the fallback for the
// sites nobody has wrapped yet, or a node something else on the page moved. It
// is the workaround from facebook/react#11538 with its caveats taken seriously:
// it only steps in where the native call would have thrown, it does the closest
// thing to what React asked for instead of silently skipping the call, and it
// reports each distinct conflict to Datadog Error Tracking with the owning React
// component, so the hotspot gets wrapped next. Never rely on it to make a bare
// {text} sibling safe.

import { trackError } from "@/lib/analytics/tracker";

export type DomConflict = {
  op: "removeChild" | "insertBefore";
  /** Parent tag plus first class, e.g. "p.quote". */
  parent: string;
  /** "#text(<length>)" or "<tag>.<class>". Never page text: it can be user input. */
  node: string;
  /** React owner chain, innermost first, e.g. "FloatingQuoteStage < MembersSection". */
  owner: string;
  /**
   * What the guard did instead of throwing:
   * - detached: the node was already out of the document; nothing to remove.
   * - reparented: the node was still attached elsewhere (a <font> wrapper), so it was removed from there.
   * - repositioned: the reference sits inside a wrapper that is our child; inserted before that wrapper.
   * - appended: the reference is gone, so the node went to the end of the parent.
   */
  resolution: "detached" | "reparented" | "repositioned" | "appended";
};

/** Hard ceiling per page load: one conflict per parent/owner is all the diagnosis needs. */
const MAX_REPORTS_PER_PAGE = 20;

const FIBER_KEY_PREFIX = "__reactFiber$";
let fiberKey: string | null = null;

function describeNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return `#text(${(node.nodeValue ?? "").length})`;
  const el = node as Element;
  const tag = el.nodeName.toLowerCase();
  const cls = el.classList?.[0];
  return cls ? `${tag}.${cls}` : tag;
}

function componentName(type: unknown): string | null {
  if (typeof type === "function") {
    const t = type as { displayName?: string; name?: string };
    return t.displayName || t.name || "anon";
  }
  if (type && typeof type === "object") {
    // memo() wraps in { type }, forwardRef() in { render }.
    const t = type as { type?: unknown; render?: unknown };
    if (t.type) return componentName(t.type);
    if (t.render) return componentName(t.render);
  }
  return null;
}

type Fiber = { type: unknown; return: Fiber | null };

/** React's fiber for a DOM node it created (elements and text nodes alike), or null. */
function fiberOf(node: Node): Fiber | null {
  const obj = node as unknown as Record<string, unknown>;
  if (!fiberKey || !(fiberKey in obj)) {
    fiberKey = Object.keys(obj).find((k) => k.startsWith(FIBER_KEY_PREFIX)) ?? null;
  }
  return fiberKey ? ((obj[fiberKey] as Fiber | undefined) ?? null) : null;
}

/** Walk React's fiber from a DOM node upward, collecting component names. */
function describeOwner(node: Node): string {
  let fiber = fiberOf(node);
  const names: string[] = [];
  while (fiber && names.length < 6) {
    const name = componentName(fiber.type);
    if (name) names.push(name);
    fiber = fiber.return;
  }
  return names.join(" < ");
}

function defaultReport(c: DomConflict): void {
  const error = new Error(`${c.op} ${c.resolution}: ${c.node} in ${c.parent}${c.owner ? ` (${c.owner})` : ""}`);
  error.name = "DomMutationConflict";
  trackError(error, { ...c, source: "domResilience" });
}

let installed: (() => void) | null = null;

/**
 * Patch Node.prototype.removeChild / insertBefore so a node another agent moved
 * no longer throws. Idempotent; returns the uninstaller. `report` receives each
 * distinct conflict (one per op/parent/owner, at most MAX_REPORTS_PER_PAGE).
 */
export function installDomResilience(report: (c: DomConflict) => void = defaultReport): () => void {
  if (installed) return installed;

  const originalRemoveChild = Node.prototype.removeChild;
  const originalInsertBefore = Node.prototype.insertBefore;
  const seen = new Set<string>();
  let reported = 0;

  // Everything diagnostic happens in here; it must never break the DOM call it describes.
  const record = (op: DomConflict["op"], parent: Node, node: Node, resolution: DomConflict["resolution"]) => {
    try {
      const conflict: DomConflict = {
        op,
        parent: describeNode(parent),
        node: describeNode(node),
        owner: describeOwner(node),
        resolution,
      };
      const key = `${op}|${conflict.parent}|${conflict.owner}`;
      if (seen.has(key) || reported >= MAX_REPORTS_PER_PAGE) return;
      seen.add(key);
      reported += 1;
      report(conflict);
    } catch {
      // ignore: diagnostics only
    }
  };

  // Only React-owned nodes get the tolerant path. Anything the native call
  // would accept, would reject with its own TypeError, or that some other
  // library owns keeps the native behaviour, so a real bug there still throws.
  const tolerated = (node: unknown): node is Node => node instanceof Node && fiberOf(node) !== null;

  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child?.parentNode === this || !tolerated(child)) {
      return originalRemoveChild.call(this, child) as T;
    }
    const actualParent = child.parentNode;
    if (actualParent) {
      // React wants this node gone; take it out of wherever the translator put it.
      originalRemoveChild.call(actualParent, child);
      record("removeChild", this, child, "reparented");
    } else {
      record("removeChild", this, child, "detached");
    }
    return child;
  };

  Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, reference: Node | null): T {
    if (!reference || reference.parentNode === this || !tolerated(reference)) {
      return originalInsertBefore.call(this, newNode, reference) as T;
    }
    // Chrome/Safari keep the reference inside a wrapper that is our child: that
    // wrapper is exactly where React meant "before the reference" to be.
    let anchor: Node | null = reference;
    while (anchor && anchor.parentNode !== this) anchor = anchor.parentNode;
    const result = originalInsertBefore.call(this, newNode, anchor) as T;
    record("insertBefore", this, reference, anchor ? "repositioned" : "appended");
    return result;
  };

  installed = () => {
    Node.prototype.removeChild = originalRemoveChild;
    Node.prototype.insertBefore = originalInsertBefore;
    installed = null;
  };
  return installed;
}
